import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './movies.css';
import { trackPageView } from '../lib/supabase';
import { fetchMovieGenres, fetchMoviesByGenre } from '../lib/menuData';
import CardImage from './CardImage';
import { classifyVideoUrl } from '../utils/videoUrl';
import VideoPlayer from './VideoPlayer';

const IA_BASE = 'https://archive.org';

// Extract the Archive.org item identifier from a download URL.
// e.g. https://archive.org/download/gullivers_travels1939/gullivers_travels1939.mp4
//   → 'gullivers_travels1939'
function extractIAIdentifier(url) {
  const m = url?.match(/archive\.org\/download\/([^/]+)\//);
  return m ? m[1] : null;
}

// Resolve the best direct MP4 for an IA identifier via the metadata API.
async function fetchDirectVideoUrl(identifier) {
  const res = await fetch(`${IA_BASE}/metadata/${identifier}`);
  if (!res.ok) throw new Error(`Archive.org API error ${res.status}`);
  const json = await res.json();
  const files = (json.files || []).filter(f => {
    const name = (f.name ?? '').toLowerCase();
    const fmt  = (f.format ?? '').toLowerCase();
    return name.endsWith('.mp4') || fmt.includes('h.264') || fmt.includes('mpeg4');
  });
  if (!files.length) throw new Error('No playable video file found for this item');
  files.sort((a, b) => parseInt(b.size ?? 0, 10) - parseInt(a.size ?? 0, 10));
  return `${IA_BASE}/download/${identifier}/${encodeURIComponent(files[0].name)}`;
}

// The movie pages only ever show this heading.
const categoryName = 'Movies';

const Movies = () => {
  const { categoryId, subcategoryId } = useParams();
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [showingCategories, setShowingCategories] = useState(false);

  // Resolved playback URL (may differ from selectedMovie.link for IA content)
  const [videoUrl, setVideoUrl]         = useState(null);
  // 'direct' | 'youtube' | 'vimeo'
  const [videoType, setVideoType]       = useState('direct');
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError]     = useState(null);

  // Escape leaves the player (or its loading/error screen) and returns to the list.
  useEffect(() => {
    if (!selectedMovie) return;
    const handleEscape = (e) => { if (e.key === 'Escape') handleBackToList(); };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMovie]);

  useEffect(() => {
    setSelectedMovie(null);

    trackPageView('Movies', parseInt(categoryId), subcategoryId ? parseInt(subcategoryId) : null);

    if (subcategoryId) {
      fetchMoviesByGenre(subcategoryId)
        .then(rows => {
          // Map Supabase columns to the shape the player expects
          setMovies(rows.map(r => ({
            title: r.title,
            description: r.description,
            link: r.link,
            thumbnail: r.thumbnail,
          })));
          setShowingCategories(false);
        })
        .catch(err => console.error('Failed to load movies:', err));
    } else {
      fetchMovieGenres()
        .then(genres => {
          // image_path is already a full Supabase Storage URL after fetchMovieGenres()
          setCategories(genres.map(g => ({
            id: g.id,
            name: g.name,
            image: g.image_path || '',
          })));
          setShowingCategories(true);
        })
        .catch(err => console.error('Failed to load genres:', err));
    }
  }, [categoryId, subcategoryId]);

  const handleMovieClick = async (movie) => {
    await trackPageView(`Movie - ${movie.title}`, parseInt(categoryId), parseInt(subcategoryId));
    setSelectedMovie(movie);
    setVideoUrl(null);
    setVideoType('direct');
    setVideoError(null);
    setVideoLoading(true);

    const { type, embedUrl } = classifyVideoUrl(movie.link);

    if (type === 'youtube' || type === 'vimeo') {
      // Embed URLs are ready immediately — no API call needed
      setVideoType(type);
      setVideoUrl(embedUrl);
      setVideoLoading(false);
      return;
    }

    const identifier = extractIAIdentifier(movie.link);
    if (identifier) {
      // IA-hosted content: resolve via metadata API for a guaranteed working URL
      fetchDirectVideoUrl(identifier)
        .then(url => { setVideoUrl(url); setVideoType('direct'); setVideoLoading(false); })
        .catch(err => { setVideoError(err.message); setVideoLoading(false); });
    } else {
      // Non-IA direct URL: use as-is
      setVideoUrl(embedUrl);
      setVideoType('direct');
      setVideoLoading(false);
    }
  };

  const handleCategoryClick = (category) => {
    // Navigate to the selected subcategory
    navigate(`/movies/${categoryId}/subcategory/${category.id}`);
  };

  const handleBackToList = async () => {
    await trackPageView(`Movies - ${categoryName}`, parseInt(categoryId), parseInt(subcategoryId));
    setSelectedMovie(null);
    setVideoUrl(null);
    setVideoType('direct');
    setVideoError(null);
    setVideoLoading(false);
  };

  // The page we land on records its own view.
  const handleBackToCategory = () => {
    navigate(subcategoryId ? `/movies/${categoryId}` : '/');
  };

  return (
    <div>
      <div className="top-bar">
        <button onClick={handleBackToCategory} className="back-button">Back</button>
        <div className="top-bar-title">{categoryName}</div>
      </div>
      <div className="top-bar-spacer" />
      {selectedMovie ? (
        videoLoading ? (
          <>
            <div className="top-bar">
              <button onClick={handleBackToList} className="back-button">Back</button>
              <div className="top-bar-title">{selectedMovie.title}</div>
            </div>
            <div className="top-bar-spacer" />
            <div style={{ padding: '4rem', textAlign: 'center', color: '#666' }}>
              <p>Loading video…</p>
            </div>
          </>
        ) : videoError ? (
          <>
            <div className="top-bar">
              <button onClick={handleBackToList} className="back-button">Back</button>
              <div className="top-bar-title">{selectedMovie.title}</div>
            </div>
            <div className="top-bar-spacer" />
            <div style={{ padding: '4rem', textAlign: 'center' }}>
              <p style={{ color: '#c00' }}>Could not load video: {videoError}</p>
              <button onClick={handleBackToList} className="back-button" style={{ marginTop: '1rem' }}>← Back to Movies</button>
            </div>
          </>
        ) : (
          <VideoPlayer
            key={videoUrl}
            url={videoUrl}
            type={videoType}
            title={selectedMovie.title}
            description={selectedMovie.description}
            onBack={handleBackToList}
            onError={setVideoError}
          />
        )
      ) : showingCategories ? (
        // Display movie categories
        <div className="categories-wrapper">
        <div className="content-grid">
          {categories.map((category, index) => (
            <div 
              key={index} 
              className="content-card"
              onClick={() => handleCategoryClick(category)}
            >
              <CardImage src={category.image} alt={category.name} />
              <div className="content-title">{category.name}</div>
            </div>
          ))}
        </div>
        </div>
      ) : (
        // Display the list of movie cards
        <div className="categories-wrapper">
          <div className="content-grid">
            {movies.length > 0 ? (
              movies.map((movie, index) => (
                <div key={index} className="content-card" onClick={() => handleMovieClick(movie)}>
                  <CardImage src={movie.thumbnail} alt={movie.title} />
                  <div className="content-title">{movie.title}</div>
                </div>
              ))
            ) : (
              <div className="no-content" role="status" style={{ gridColumn: '1/-1', padding: '2rem', color: '#666' }}>
                No movies found for this category.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Movies;
